import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { ClientGroupTypeConfig } from 'models/flexible/ClientGroupType';
import { PERMISSIONS } from 'models/Profile';
import { adminUpdateClientGroupType } from 'services/api/admin/clientGroupTypes';
import { adminShowClient } from 'services/api/admin/clients';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import ConfigureCareOptionsForm from './ConfigureCareOptionsForm';

type Configs = Record<string, ClientGroupTypeConfig>;

const INCLUDES = ['managedClientGroupTypes.groupType'] as const;

function deepEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function ConfigureCareOptions() {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const disabled = !profile.has(PERMISSIONS.adminClientGroupTypeEdit);

  const { data: client, isLoading } = useQuery({
    queryKey: ['adminClient', id, INCLUDES],
    queryFn: ({ signal }) => adminShowClient(id!, signal, INCLUDES),
    enabled: !!id,
  });

  const managedClientGroupTypes = client?.managedClientGroupTypes ?? [];

  // Build initial configs from server data
  const serverConfigs = useRef<Configs>({});
  const [configs, setConfigs] = useState<Configs>({});
  const [selectedTabId, setSelectedTabId] = useState<string>('');

  useEffect(() => {
    if (!managedClientGroupTypes.length) return;

    const built: Configs = {};
    for (const cgt of managedClientGroupTypes) {
      built[cgt.id] = cgt.config as ClientGroupTypeConfig;
    }
    serverConfigs.current = built;
    setConfigs(built);

    if (!selectedTabId || !built[selectedTabId]) {
      setSelectedTabId(managedClientGroupTypes[0].id);
    }
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = !deepEqual(configs, serverConfigs.current);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (updatedConfigs: Configs) => {
      const updates = Object.entries(updatedConfigs).filter(
        ([cgtId, config]) => !deepEqual(config, serverConfigs.current[cgtId])
      );

      await Promise.all(
        updates.map(([cgtId, config]) =>
          adminUpdateClientGroupType(cgtId, {
            caseManager: config.caseManager,
            launchDate: config.launchDate ?? null,
            projectedDischarge: config.projectedDischarge,
            referral: config.referral,
            safeReturn: config.safeReturn,
            utilizationManagement: config.utilizationManagement,
            utilizationManager: config.utilizationManager,
          })
        )
      );
    },
    onSuccess: () => {
      serverConfigs.current = { ...configs };
      addToast({ text: 'Care option(s) successfully updated' });
    },
    onError: () => {
      addToast({ text: 'An error occurred while saving. Please try again.' });
    },
  });

  const handleSave = () => mutateAsync(configs);
  const handleDiscard = () => setConfigs({ ...serverConfigs.current });

  const tabs: TabType[] = managedClientGroupTypes.map((cgt: any) => ({
    label: cgt.groupType?.displayName ?? cgt.groupTypeId,
    value: cgt.id,
  }));

  const selectedTab = tabs.find((t) => t.value === selectedTabId);
  const selectedConfig = configs[selectedTabId];
  const selectedGroupTypeName = selectedTab?.label;

  if (isLoading) {
    return <CircularProgress style={{ margin: '48px auto', display: 'block' }} />;
  }

  return (
    <PageContainer>
      <Header>
        <HeaderTop>
          <TitleArea>
            <Title>Configure Managed Care Options</Title>
            <Subtitle>{client?.name}</Subtitle>
          </TitleArea>
          {!disabled && (
            <ButtonGroup>
              <Button variant='ghost' onClick={handleDiscard} disabled={!isDirty || isPending}>
                Discard Changes
              </Button>
              <Button data-cy='saveConfigureCareOptionsButton' onClick={handleSave} disabled={!isDirty || isPending}>
                Save Changes
              </Button>
            </ButtonGroup>
          )}
        </HeaderTop>
        {tabs.length > 1 && (
          <TabsWrapper data-cy='careOptionsTabs'>
            <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedTabId(tab.value)} />
          </TabsWrapper>
        )}
      </Header>

      <Content>
        {selectedConfig && id && (
          <ConfigureCareOptionsForm
            config={selectedConfig}
            onChange={(updated) => setConfigs((prev) => ({ ...prev, [selectedTabId]: updated }))}
            disabled={disabled}
            clientId={id}
            groupTypeName={selectedGroupTypeName}
          />
        )}
      </Content>
    </PageContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: white;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
  color: ${colors.black100};
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: ${colors.blue100};
`;

const TabsWrapper = styled.div`
  border-top: 1px solid ${colors.black10};
  padding: 0 24px;
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px;
`;
